import {
  forceReLogin,
  getAuthToken,
  refreshAccessTokenShared,
} from "@/lib/auth/authService";
import { patientConciergeApiBase } from "@/lib/api-base";

/** Signed-in profile tenant for staff pages when URL has no tenantKey. */
let staffTenantFallback = "";

/** Prefer URL `tenantKey`; fall back to the value set by staff pages from the user profile. */
export function setStaffTenantFallback(
  tenantKey: string | null | undefined,
): void {
  staffTenantFallback = tenantKey?.trim() || "";
}

function resolveTenantKey(): string {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const fromUrl =
      params.get("tenantKey")?.trim() || params.get("tenant_key")?.trim() || "";
    if (fromUrl) return fromUrl;
  }
  return staffTenantFallback;
}

function apiUrl(path: string): string {
  const base = patientConciergeApiBase();
  if (base) {
    return `${base}/v1/patient-concierge/management/${path}`;
  }
  // Local Rsbuild proxy (see rsbuild.config.ts).
  return `/api/management/${path}`;
}

export function authHeaders(): HeadersInit {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function isUnauthorized(res: Response): boolean {
  return res.status === 401 || res.status === 403;
}

async function rawFetch(
  pathWithQuery: string,
  init?: RequestInit,
): Promise<Response> {
  return fetch(apiUrl(pathWithQuery), {
    ...init,
    cache: "no-store",
    headers: {
      ...authHeaders(),
      ...(init?.headers ?? {}),
    },
  });
}

/**
 * Management API fetch. On 401/403: try refresh once and retry; if that fails, re-login.
 */
export async function fetchManagementJson(
  pathWithQuery: string,
  init?: RequestInit,
): Promise<Response> {
  let res = await rawFetch(pathWithQuery, init);
  if (!isUnauthorized(res)) return res;

  const refreshed = await refreshAccessTokenShared();
  if (refreshed) {
    res = await rawFetch(pathWithQuery, init);
    if (!isUnauthorized(res)) return res;
  }

  forceReLogin();
  return res;
}

export function managementTenantKey(): string {
  return resolveTenantKey();
}

export type ManagementDevice = {
  deviceId: string;
  deviceAlias?: string | null;
  deviceName?: string | null;
  organizationId?: number | null;
  organizationSiteId?: number | null;
  organizationSiteLocationId?: number | null;
  organizationName?: string | null;
  siteName?: string | null;
  locationName?: string | null;
  room?: string | null;
  bed?: string | null;
};

export async function fetchDevices(options?: {
  tenantKey?: string;
  organizationId?: number | null;
  organizationSiteId?: number | null;
  organizationSiteLocationId?: number | null;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  ok: boolean;
  devices: ManagementDevice[];
  totalCount: number;
  page: number;
  pageSize: number;
  message?: string;
}> {
  const page = options?.page && options.page > 0 ? options.page : 1;
  const pageSize =
    options?.pageSize && options.pageSize > 0 ? options.pageSize : 20;
  const tenantKey = (options?.tenantKey ?? resolveTenantKey()).trim();
  if (!tenantKey) {
    return {
      ok: false,
      devices: [],
      totalCount: 0,
      page,
      pageSize,
      message: "tenantKey is required.",
    };
  }
  const params = new URLSearchParams();
  params.set("tenantKey", tenantKey);
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  if (options?.organizationId) {
    params.set("organizationId", String(options.organizationId));
  }
  if (options?.organizationSiteId) {
    params.set("organizationSiteId", String(options.organizationSiteId));
  }
  if (options?.organizationSiteLocationId) {
    params.set(
      "organizationSiteLocationId",
      String(options.organizationSiteLocationId),
    );
  }
  if (options?.search?.trim()) {
    params.set("search", options.search.trim());
  }

  const res = await fetchManagementJson(`devices?${params}`);
  let body: Record<string, unknown> = {};
  try {
    body = (await res.json()) as Record<string, unknown>;
  } catch {
    /* ignore */
  }

  const ok = res.ok && Boolean(body.isSuccess ?? body.IsSuccess ?? true);
  const raw =
    (body.devices as Record<string, unknown>[] | undefined) ??
    (body.Devices as Record<string, unknown>[] | undefined) ??
    [];

  const devices: ManagementDevice[] = raw.map((d) => ({
    deviceId: String(d.deviceId ?? d.DeviceId ?? ""),
    deviceAlias: (d.deviceAlias ?? d.DeviceAlias) as string | null | undefined,
    deviceName: (d.deviceName ?? d.DeviceName) as string | null | undefined,
    organizationId: (d.organizationId ?? d.OrganizationId) as
      | number
      | null
      | undefined,
    organizationSiteId: (d.organizationSiteId ?? d.OrganizationSiteId) as
      | number
      | null
      | undefined,
    organizationSiteLocationId: (d.organizationSiteLocationId ??
      d.OrganizationSiteLocationId) as number | null | undefined,
    organizationName: (d.organizationName ?? d.OrganizationName) as
      | string
      | null
      | undefined,
    siteName: (d.siteName ?? d.SiteName) as string | null | undefined,
    locationName: (d.locationName ?? d.LocationName) as
      | string
      | null
      | undefined,
    room: (d.room ?? d.Room) as string | null | undefined,
    bed: (d.bed ?? d.Bed) as string | null | undefined,
  }));

  return {
    ok,
    devices: devices.filter((d) => d.deviceId),
    totalCount: Number(body.totalCount ?? body.TotalCount ?? devices.length),
    page: Number(body.page ?? body.Page ?? page),
    pageSize: Number(body.pageSize ?? body.PageSize ?? pageSize),
    message: ok
      ? undefined
      : String(body.message ?? body.Message ?? "Failed to load devices"),
  };
}

export async function acceptPatientRequest(
  patientRequestId: string | number,
  note?: string,
  tenantKey?: string,
): Promise<{ ok: boolean; status?: string; message?: string }> {
  const key = (tenantKey ?? resolveTenantKey()).trim();
  const res = await fetchManagementJson(
    `requests/${patientRequestId}/accept?tenantKey=${encodeURIComponent(key)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: note ?? null }),
    },
  );

  let body: {
    isSuccess?: boolean;
    IsSuccess?: boolean;
    status?: string;
    Status?: string;
    message?: string;
    Message?: string;
  } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* ignore */
  }

  const ok = res.ok && (body.isSuccess ?? body.IsSuccess ?? true);
  return {
    ok,
    status: body.status ?? body.Status,
    message: body.message ?? body.Message,
  };
}

export async function fetchAblyBoardToken(tenantKey?: string): Promise<{
  ok: boolean;
  channelName?: string;
  tenantKey?: string;
  tokenRequest?: unknown;
  message?: string;
}> {
  const key = (tenantKey ?? resolveTenantKey()).trim();
  const res = await fetchManagementJson(
    `ably/token?tenantKey=${encodeURIComponent(key)}`,
  );

  let body: {
    isSuccess?: boolean;
    IsSuccess?: boolean;
    channelName?: string;
    ChannelName?: string;
    tenantKey?: string;
    TenantKey?: string;
    tokenRequest?: unknown;
    TokenRequest?: unknown;
    message?: string;
    Message?: string;
  } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* ignore */
  }

  const tokenRequest = body.tokenRequest ?? body.TokenRequest;
  const ok =
    res.ok &&
    (body.isSuccess ?? body.IsSuccess ?? false) &&
    tokenRequest != null;

  return {
    ok,
    channelName: body.channelName ?? body.ChannelName,
    tenantKey: body.tenantKey ?? body.TenantKey,
    tokenRequest,
    message: body.message ?? body.Message,
  };
}
